import type { ReactNode } from 'react';
import { Button, Spinner } from './Button.js';
import { Logo } from '../Logo.js';
import { useTheme } from '../../lib/theme.js';

/** Loading: never a blank screen, never a layout jump. */
export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex min-h-40 items-center justify-center gap-3 text-text-muted" role="status">
      <Spinner />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function Skeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4" role="status" aria-label="Loading content">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-8 animate-pulse rounded-lg bg-bg-subtle" />
      ))}
    </div>
  );
}

/** Empty: what would appear here, and the action to create it when allowed. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  const { theme } = useTheme();
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-2 p-6 text-center">
      <Logo
        variant="mark"
        tone={theme === 'dark' ? 'mono-lime' : 'mono-navy'}
        height={28}
        title="HospaSense mark"
        style={{ opacity: 0.35 }}
      />
      <p className="text-sm font-medium text-text-primary">{title}</p>
      <p className="max-w-sm text-xs text-text-muted">{description}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Error: say what failed in human words and offer Retry. */
export function ErrorState({
  title = 'Something went wrong',
  description,
  onRetry,
}: {
  title?: string;
  description: string;
  onRetry?: () => void;
}) {
  return (
    <div
      className="flex min-h-40 flex-col items-center justify-center gap-2 p-6 text-center"
      role="alert"
    >
      <p className="text-sm font-medium text-error">{title}</p>
      <p className="max-w-sm text-xs text-text-muted">{description}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
          Retry
        </Button>
      )}
    </div>
  );
}

/** Permission denied: explicit, never a blank or misleading screen. */
export function PermissionDeniedState({ what }: { what: string }) {
  return (
    <div
      className="flex min-h-40 flex-col items-center justify-center gap-2 p-6 text-center"
      role="alert"
    >
      <p className="text-sm font-medium text-text-primary">You don’t have access to {what}</p>
      <p className="max-w-sm text-xs text-text-muted">
        Your role doesn’t grant this permission. Access is deny-by-default; ask a Product Owner if
        you believe this is wrong.
      </p>
    </div>
  );
}
